import { Notification } from '@/shared/ui/notifications/notifications';
import { createContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { toast } from 'react-toastify';

type FullscreenContextValue = {
    isFullscreen: boolean;
    toggleFullscreen: () => Promise<void>;
};

export const FullscreenContext = createContext<FullscreenContextValue | undefined>(undefined);

type FullscreenProviderProps = {
    children: ReactNode;
};

export function FullscreenProvider({ children }: FullscreenProviderProps) {
    const [isFullscreen, setIsFullscreen] = useState<boolean>(Boolean(document.fullscreenElement));

    useEffect(() => {
        const handleChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
        };

        document.addEventListener('fullscreenchange', handleChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleChange);
        };
    }, []);

    useEffect(() => {
        document.body.dataset.fullscreen = isFullscreen ? 'true' : 'false';
    }, [isFullscreen]);

    const toggleFullscreen = useCallback(async () => {
        try {
            if (!document.fullscreenElement) {
                await document.documentElement.requestFullscreen();
            } else {
                await document.exitFullscreen();
            }
        } catch {
            toast(<Notification type="warning" text="Failed to toggle fullscreen mode" />, {
                style: {
                    border: '1px solid #eb5757',
                },
            });
        }
    }, []);

    return (
        <FullscreenContext.Provider value={{ isFullscreen, toggleFullscreen }}>{children}</FullscreenContext.Provider>
    );
}
